import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';
import cardBackgroundImage from '../cardbackground.webp';
import buttonImage from '../buttonimage.webp';
import journalBackgroundImage from '../journalbackgroundimage.webp';

const Journal = ({ goBack, goHome }) => {
    const [entry, setEntry] = useState('');
    const [savedEntries, setSavedEntries] = useState(null);
    const [statusMsg, setStatusMsg] = useState('');
    const statusRef = useRef(null);

    useEffect(() => {
        const fetchSavedEntries = async () => {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
                setStatusMsg('User not logged in!');
                return;
            }
            const { data, error } = await supabase
                .rpc('decrypt_journal_entry')
                .eq('user_id', user.id);
            if (error) {
                setStatusMsg('Failed to fetch journal entries: ' + error.message);
                return;
            }
            setSavedEntries(data || []);
        };
        fetchSavedEntries();
    }, []);

    const handleSave = async () => {
        try {
            const { data: { user }, error: userError } = await supabase.auth.getUser();
            if (userError || !user) {
                setStatusMsg('User not logged in!');
                return;
            }
            const { data, error } = await supabase
                .from('journal_entries')
                .insert({
                    user_id: user.id,
                    text: entry.trim(),
                    date: new Date().toISOString(),
                })
                .select('id, user_id, date, text')
                .single();
            if (error) {
                setStatusMsg(`Failed to save journal entry: ${error.message}`);
                return;
            }
            // Fetch decrypted entry
            const { data: decryptedData, error: decryptError } = await supabase
                .rpc('decrypt_journal_entry')
                .eq('id', data.id)
                .single();
            if (decryptError) {
                setStatusMsg('Entry saved, but failed to display: ' + decryptError.message);
                return;
            }
            setStatusMsg('Journal entry saved successfully!');
            setEntry('');
            setSavedEntries([decryptedData, ...(savedEntries || [])]);
        } catch (err) {
            setStatusMsg('An unexpected error occurred. Please try again.');
        }
    };

    const handleDeleteEntry = async (id, entryText) => {
        // Confirm with user (screen reader friendly)
        const confirmed = window.confirm('Are you sure you want to delete this journal entry? This action cannot be undone.');
        if (!confirmed) {
            setStatusMsg('Delete cancelled.');
            return;
        }
        try {
            const { data: { user }, error: userError } = await supabase.auth.getUser();
            if (userError || !user) {
                setStatusMsg('User not logged in!');
                return;
            }
            const { error } = await supabase
                .from('journal_entries')
                .delete()
                .eq('id', id)
                .eq('user_id', user.id);
            if (error) {
                setStatusMsg(`Failed to delete journal entry: ${error.message}`);
                return;
            }
            setSavedEntries((prev) => prev.filter((entry) => entry.id !== id));
            setStatusMsg('Journal entry deleted successfully!');
        } catch (err) {
            setStatusMsg('An unexpected error occurred. Please try again.');
        }
    };

    return (
        <div style={{
            backgroundImage: `url(${journalBackgroundImage})`,
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '16px',
        }}>
            <h2 style={{
                fontFamily: 'Times New Roman, Times, serif',
                fontSize: 'clamp(24px, 4vw, 36px)',
                fontWeight: 'bold',
                color: 'black',
                textAlign: 'center',
                marginTop: '16px',
            }}>Journal</h2>
            {/* Visually hidden screen reader instructions */}
            <div id="journal-instructions" style={{ position: 'absolute', left: '-9999px', height: '1px', width: '1px', overflow: 'hidden' }} aria-hidden="false">
                This is your journal page. Type your entry in the text box. To save, press the Save Entry button below. Below the navigation buttons, you will find a list of your saved entries. Each entry has a delete button. To delete an entry, press the delete button and confirm when prompted. Status messages will be announced.
            </div>
            {/* Status message for screen readers */}
            <div
                ref={statusRef}
                aria-live="polite"
                aria-atomic="true"
                style={{ position: 'absolute', left: '-9999px', height: '1px', width: '1px', overflow: 'hidden' }}
            >
                {statusMsg}
            </div>
            <div style={{
                maxWidth: '752px',
                width: '100%',
                backgroundImage: `url(${cardBackgroundImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                border: '2px solid #4B5563',
                borderRadius: '8px',
                boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.3)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                margin: '16px auto',
                padding: '16px',
            }}>
                <label htmlFor="journal-entry" style={{
                    fontFamily: 'Times New Roman, Times, serif',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    marginBottom: '8px',
                }}>Write your journal entry:</label>
                <textarea
                    id="journal-entry"
                    aria-describedby="journal-instructions"
                    value={entry}
                    onChange={(e) => setEntry(e.target.value)}
                    placeholder="Write your journal entry here..."
                    style={{
                        width: '100%',
                        height: '200px',
                        padding: '8px',
                        marginBottom: '16px',
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: '16px',
                        borderRadius: '8px',
                        border: '1px solid #ccc',
                        backgroundColor: 'white',
                        resize: 'none',
                    }}
                />
                <button
                    aria-label="Save journal entry"
                    style={{
                        backgroundImage: `url(${buttonImage})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: '12px 24px',
                        width: '100%',
                        maxWidth: '240px',
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: 'clamp(14px, 2vw, 16px)',
                        fontWeight: 'bold',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                        outline: 'none',
                        cursor: 'pointer',
                    }}
                    onClick={handleSave}
                >
                    Save Entry
                </button>
            </div>
            <div style={{
                marginTop: '16px',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                maxWidth: '256px',
                gap: '16px',
            }}>
                <button
                    aria-label="Go back"
                    style={{
                        backgroundImage: `url(${buttonImage})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: '12px 24px',
                        width: '100%',
                        maxWidth: '120px',
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: 'clamp(14px, 2vw, 16px)',
                        fontWeight: 'bold',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                        outline: 'none',
                        cursor: 'pointer',
                    }}
                    onClick={goBack}
                >Back</button>
                <button
                    aria-label="Go home"
                    style={{
                        backgroundImage: `url(${buttonImage})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: '12px 24px',
                        width: '100%',
                        maxWidth: '120px',
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: 'clamp(14px, 2vw, 16px)',
                        fontWeight: 'bold',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                        outline: 'none',
                        cursor: 'pointer',
                    }}
                    onClick={goHome}
                >Home</button>
            </div>
            <div style={{
                marginTop: '16px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '16px',
                width: '100%',
                maxWidth: '768px',
            }}>
                <h3 style={{
                    fontFamily: 'Times New Roman, Times, serif',
                    fontSize: 'clamp(20px, 3vw, 36px)',
                    fontWeight: 'bold',
                    color: 'black',
                    textAlign: 'center',
                    marginBottom: '8px',
                }}>Saved Entries</h3>
                {savedEntries && [...savedEntries]
                    .sort((a, b) => new Date(b.date) - new Date(a.date))
                    .map((savedEntry, index) => (
                        <div key={index} style={{
                            width: '100%',
                            border: '2px solid #4B5563',
                            borderRadius: '8px',
                            padding: '16px',
                            boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.3)',
                            backgroundColor: 'white',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}>
                            <p
                                style={{
                                    fontFamily: 'Times New Roman, Times, serif',
                                    fontSize: '16px',
                                    color: '#4B5563',
                                    textAlign: 'left',
                                    margin: 0,
                                    flex: 1,
                                    wordBreak: 'break-word',
                                }}
                                aria-label={`Journal entry from ${new Date(savedEntry.date).toLocaleDateString()}: ${savedEntry.entry}`}
                            >
                                {new Date(savedEntry.date).toLocaleDateString()} - {savedEntry.entry}
                            </p>
                            <button
                                aria-label={`Delete journal entry from ${new Date(savedEntry.date).toLocaleDateString()}`}
                                style={{
                                    color: '#F90C0C',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                    marginLeft: '16px',
                                    userSelect: 'none',
                                    textDecoration: 'underline',
                                    fontWeight: 'bold',
                                    padding: 0,
                                    WebkitTextFillColor: '#F90C0C', // for Safari/Chrome
                                }}
                                onClick={() => handleDeleteEntry(savedEntry.id, savedEntry.entry)}
                            >
                                Delete
                            </button>
                        </div>
                    ))}
            </div>
        </div>
    );
};

export default Journal;